import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthAppService } from 'avn/services/auth-app/auth-app.service';
import { SharedCompsService } from 'avn/shared/shared-comps.service';
import { toUrlSlug } from 'avn/shared/utils/url-slug.util';
import * as moment from 'moment';
import { BehaviorSubject, catchError, map, of, tap } from 'rxjs';

import { ApiService } from '../../../../services/api.service';

// Define the component with its metadata
@Component({
  selector: 'app-search-item-practitioner',
  templateUrl: './search-item-practitioner.component.html',
  styleUrls: ['./search-item-practitioner.component.scss']
})
export class SearchItemPractitionerComponent implements AfterViewInit, OnInit, OnChanges {
  @Input() partner: any; // Input property to receive partner data
  @Input() selectedDate = moment(); // Input property to set the selected date with default value
  @Input() bookingType: 'In Person' | 'Online' = 'In Person';
  @Input() filters: any = {};
  @Input() practitioner_rating: any;
  @Output() reduceCount = new EventEmitter(); // Output event emitter to notify parent component

  // Dependency injection of various services and DOM document
  constructor(
    private authService: AuthAppService,
    private api: ApiService,
    private router: Router,
    private shared: SharedCompsService,
    private el: ElementRef,
    @Inject(DOCUMENT) private dom: any
  ) {}

  readMore = false; // Toggle for displaying full profile text
  profileLimit = 186; // Limit for profile text length
  showCard = true; // Control visibility of the card
  isMobile = false; // Flag to indicate mobile view
  loading = false; // Flag to indicate loading state

  private timeSlotsSubject = new BehaviorSubject<any[]>([]);
  timeSlots$ = this.timeSlotsSubject.asObservable(); // Observable for time slots data
  slotsAvailability$: BehaviorSubject<any> = new BehaviorSubject<any>({}); // Observable to track slots availability
  slotsAvailability: any = {}; // Slots availability data

  slotParams: any = {}; // Parameters for slots

  pracWidth = false;
  focusChunk = 4;
  focusAreas: string[] = [];
  desktopFocusVisible = this.focusChunk;
  mobileFocusVisible = this.focusChunk;
  currentWeekStart: moment.Moment | null = null;
  nextStartDate: moment.Moment | null = null;
  loadingMoreSlots = false;
  selectedDay: any = null;
  selectedDayParts: any[] = [];
  selectedDayFlatSlots: { key: string; time: string }[] = [];

  ngOnInit(): void {
    const width = this.getViewportWidth();
    this.setLayoutForWidth(width);
    this.updateFocusAreas();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['partner']) {
      this.updateFocusAreas();
    }
  }

  // Lifecycle hook that triggers after view is initialized
  ngAfterViewInit(): void {
    this.observePracTimeSlots(); // Start observing practitioner time slots
    this.pracWidthCheck();
  }

  // Listener for window resize events to adjust profile text limit
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    const width = (event.target as Window)?.innerWidth || 0;
    this.setLayoutForWidth(width);
    this.pracWidthCheck();
  }

  pracWidthCheck() {
    const hostElement = this.el?.nativeElement as HTMLElement;
    if (hostElement) {
      const container =
        (hostElement.querySelector('.search-card') as HTMLElement) ||
        (hostElement.querySelector('.search-card-mobile') as HTMLElement);
      if (container) {
        const width = container.offsetWidth;
        this.pracWidth = width <= 730;
      }
    }
  }

  // Observe intersection of element to load data when visible
  observePracTimeSlots() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.loading) {
          this.fetchAvailability(); // Fetch availability when element is in view
          observer.unobserve(this.el.nativeElement); // Stop observing after loading
        }
      });
    });
    observer.observe(this.el.nativeElement); // Start observing the element
  }

  // Fetch practitioner availability data
  fetchAvailability() {
    this.loading = true; // Set loading to true
    const timezone = this.filters.timezone || this.shared.getTimezone(); // Get Australian timezone
    const converted = this.shared.convertedCurrentTimeWithTimezone(timezone); // Get converted current time with timezone
    const current_date = converted.format('YYYY-MM-DD hh:mm A');
    const setting_data = moment(this.selectedDate).format('YYYY-MM-DD');

    // Fetch earliest available dates from the API
    this.api
      .earliestDates(this.partner.id, this.partner.service, setting_data, current_date, timezone)
      .pipe(
        tap((slotAvail: any) => {
          if (slotAvail.appt_date && this.partner.service) {
            this.slotsAvailability = slotAvail;
            this.slotsAvailability$.next(slotAvail); // Emit availability data
            this.currentWeekStart = moment(slotAvail.appt_date, 'YYYY-MM-DD');
            this.nextStartDate = null;
            this.timeSlotsSubject.next([]);
            this.clearSelectedDay();
            if (this.partner.id && this.partner.service && this.slotsAvailability.appt_date) {
              this.fetchTimeSlots(current_date, timezone); // Fetch time slots if data is available
            }
          } else {
            this.loading = false; // Set loading to false if no data available
          }
        }),
        catchError(() => {
          this.loading = false; // Stop loading on error
          return of({}); // Return empty object on error
        })
      )
      .subscribe();
  }

  // Fetch available time slots based on availability
  fetchTimeSlots(current_date: string, timezone: string, append = false, startOverride?: moment.Moment) {
    const baseStart = startOverride?.clone() || this.nextStartDate?.clone() || this.currentWeekStart?.clone();
    const startDate = baseStart || moment();
    const endDate = startDate.clone().add(6, 'days');
    if (append) {
      this.loadingMoreSlots = true;
    } else {
      this.loading = true;
    }
    const params = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      current_date: current_date,
      practitioner_id: this.partner.id,
      service_id: this.partner.service,
      timezone,
      online: false
    };
    // Call API to get available time slots and process response
    this.api
      .getAvailableTimes(params)
      .pipe(
        map((response1: any) => this.buildWeeklySlots(response1)),
        tap((newSlots: any[]) => {
          const merged = append ? [...this.timeSlotsSubject.value, ...newSlots] : newSlots;
          this.timeSlotsSubject.next(merged);
          this.nextStartDate = endDate.clone().add(1, 'day');
          this.loading = false;
          this.loadingMoreSlots = false;
          if (!append) {
            this.clearSelectedDay();
          }
        }),
        catchError(() => {
          this.loading = false;
          this.loadingMoreSlots = false;
          return of([]);
        })
      )
      .subscribe();
  }

  // Navigate to booking page with selected time
  selectTime(time: string, appointmentDate?: string) {
    const appointment_date = appointmentDate || this.slotsAvailability?.appt_date?.split('T')?.[0] || '';
    const service_id =
      this.slotsAvailability?.service?.uuid ||
      this.partner?.service_uuid ||
      this.partner?.service?.uuid ||
      this.partner?.service_id ||
      this.partner?.service;
    const prac_id = this.slotsAvailability?.practitioner?.id || this.partner.id;
    const business_slug = this.partner.business.slug;
    const bookingCode = this.bookingType === 'Online' ? 'o' : 'p';
    const serviceParam = bookingCode ? `${service_id}a${bookingCode}` : `${service_id}a`;
    sessionStorage.setItem('preselected_slot_time', time || '');
    sessionStorage.setItem('preselected_slot_date', appointment_date || '');
    sessionStorage.setItem('skip_slot_selection', time ? 'true' : 'false');
    this.shared.goToBooking({
      business: business_slug,
      service: serviceParam,
      prac: prac_id,
      date: appointment_date,
      time
    });
  }

  // Get limited or full profile text based on readMore toggle
  getProfileText() {
    if (!this.readMore && this.partner.profile && this.partner.profile.length > this.profileLimit) {
      const text = this.partner.profile.substring(0, this.profileLimit);
      return text + '...';
    }
    return this.partner.profile;
  }

  // Replace special characters in a text
  replaceSplCharecters(text: string) {
    return text.replace(/-/g, ' ');
  }

  showMoreFocusDesktop() {
    this.desktopFocusVisible = Math.min(this.desktopFocusVisible + this.focusChunk, this.focusAreas.length);
  }

  showMoreFocusMobile() {
    this.mobileFocusVisible = Math.min(this.mobileFocusVisible + this.focusChunk, this.focusAreas.length);
  }

  getDesktopFocusRemaining() {
    return Math.max(this.focusAreas.length - this.desktopFocusVisible, 0);
  }

  getMobileFocusRemaining() {
    return Math.max(this.focusAreas.length - this.mobileFocusVisible, 0);
  }

  buildWeeklySlots(response: any) {
    if (!response || typeof response !== 'object') {
      return [];
    }
    const dates = Object.keys(response).sort();
    const baseDays = dates.map((dateKey) => {
      const entry = response[dateKey] || {};
      const status = entry?.status || 'Closed';
      const parts = ['Morning', 'Afternoon', 'Evening']
        .map((label) => {
          const slots = Array.isArray(entry?.[label]) ? entry[label] : [];
          return {
            key: label.toLowerCase(),
            label,
            count: slots.length,
            slots
          };
        })
        .filter((part) => part.count > 0);
      const totalSlots = status === 'Open' ? parts.reduce((sum, part) => sum + part.count, 0) : 0;
      const momentDate = moment(dateKey, 'YYYY-MM-DD');
      const firstSlot = this.getFirstSlotFromParts(parts);
      return {
        id: dateKey,
        date: dateKey,
        label: this.getDayLabelForDate(momentDate),
        displayDate: momentDate.isValid() ? momentDate.format('MMM D') : '',
        totalSlots,
        appointmentsText: this.formatAppointmentText(totalSlots),
        firstSlot,
        parts,
        status
      };
    });
    return this.mergeConsecutiveUnavailable(baseDays);
  }

  getDayLabelForDate(momentDate: moment.Moment) {
    if (!momentDate || !momentDate.isValid()) {
      return '';
    }
    if (momentDate.isSame(moment(), 'day')) {
      return 'Today';
    }
    if (momentDate.isSame(moment().add(1, 'day'), 'day')) {
      return 'Tomorrow';
    }
    return momentDate.format('ddd');
  }

  // Fetch next available date
  fetchNextAvailableDate() {
    if (this.slotsAvailability && this.slotsAvailability.appt_date) {
      // const appointment_date = this.slotsAvailability.appt_date.split('T')[0];
      // return `Available from ${this.isTodayTomorrow()} ${moment(appointment_date, 'YYYY-MM-DD hh:mm A').format(
      //   'ddd, DD MMM'
      // )}`;
      return '';
    }
    return `<span class="no-avail-txt">${this.partner.name} is fully booked out this week. View all availability to see their next available date.</span>`;
  }

  // Check if appointment date is today or tomorrow
  isTodayTomorrow() {
    if (this.slotsAvailability && this.slotsAvailability.appt_date) {
      const appointment_date = this.slotsAvailability.appt_date.split('T')[0];
      const date = moment(appointment_date).endOf('day');
      const today = moment().endOf('day');
      const tomorrow = moment().add(1, 'day').endOf('day');

      if (date.isSame(today)) return 'Today ';
      else if (date.isSame(tomorrow)) return 'Tomorrow ';
    }
    return '';
  }

  // Generate URL for provider page
  getProviderPageUrl() {
    if (this.partner && this.partner.business) {
      const url = `/provider/${this.partner.business.slug}/${toUrlSlug(
        this.partner?.business?.business_location?.city
      )}`;
      return [url];
    }
    return [''];
  }

  // Navigate to specific practitioner's page
  navigateToPractitionerPageUrl(partner: any) {
    if (partner && partner.business) {
      const query = this.shared.parseQueryString(this.router.url);
      const url = `/provider/${this.partner.business.slug}/${toUrlSlug(
        this.partner?.business?.business_location?.city
      )}/practitioner/${this.partner?.uuid}/${this.partner?.service_uuid}`;
      this.router.navigate([url], { queryParams: query });
    }
  }

  // Check if the user is logged in
  hasLoggedIn() {
    return this.authService.isLoggedIn;
  }
  // Save item to lists
  saveItemToLists() {
    this.shared.openSaveListPop({
      business_id: this.partner.business.id,
      item_id: this.partner.id,
      service_id: this.partner.service,
      item_type: 'Practitioner'
    });
  }

  // Show login prompt to save items to list
  loginPage() {
    this.router.navigate(['login']);
    this.shared.showSnackBar('Login to save into your lists', 3);
  }

  getLocation() {
    if (this.bookingType === 'Online') {
      return '';
    }
    const loc = this.partner?.business?.business_location;
    return this.shared.getLocation(loc, '{{city}} {{postal_code}}');
  }
  getDisplayName(partner: any): string {
    if (!partner) {
      return '';
    }

    const rawPreferred = (partner.preferred_name || '').toString().trim();
    const rawName = (partner.name || '').toString().trim();

    // Split on any whitespace and filter out empty tokens
    const prefParts = rawPreferred.split(/\s+/).filter(Boolean);
    const nameParts = rawName.split(/\s+/).filter(Boolean);

    // 1) If preferred name has two or more words, display it as-is
    if (prefParts.length >= 2) {
      return rawPreferred;
    }

    // 2) If preferred name is a single word AND the full name has two or more words,
    //    display: `<preferred> <last word of full name>`
    if (prefParts.length === 1 && nameParts.length >= 2) {
      const lastName = nameParts[nameParts.length - 1];
      return `${prefParts[0]} ${lastName}`;
    }

    // Fallbacks: show preferred if present, otherwise the full name
    if (rawPreferred) {
      return rawPreferred;
    }
    return rawName;
  }

  bookFirstSlot(date: string, time?: string) {
    if (!time) {
      return;
    }
    this.selectTime(time, date);
  }

  showMoreSlots(currentDate: string) {
    const timezone = this.filters.timezone || this.shared.getTimezone();
    const converted = this.shared.convertedCurrentTimeWithTimezone(timezone);
    const current_date = converted.format('YYYY-MM-DD hh:mm A');
    const nextStart = this.nextStartDate ? this.nextStartDate.clone() : moment(currentDate, 'YYYY-MM-DD').add(7, 'day');
    this.fetchTimeSlots(current_date, timezone, true, nextStart);
  }

  selectDay(day: any) {
    if (!day || !day.totalSlots) {
      return;
    }
    if (this.selectedDay?.id === day.id) {
      this.clearSelectedDay();
      return;
    }
    this.selectedDay = day;
    this.selectedDayParts = day.parts || [];
    this.selectedDayFlatSlots = this.flattenSlots(this.selectedDayParts);
  }

  trackDay(index: number, day: any) {
    return day?.id || day?.date || index;
  }

  getReviewCount(): number {
    const details = this.partner?.details?.data;
    if (!details) {
      return 0;
    }
    return (
      details?.business_rating?.count ??
      details?.business?.reviews_count ??
      details?.business?.review_count ??
      details?.business?.rating_count ??
      details?.business?.reviews?.length ??
      0
    );
  }

  private getViewportWidth(): number {
    if (this.dom?.documentElement?.clientWidth) {
      return this.dom.documentElement.clientWidth;
    }
    if (typeof window !== 'undefined' && window?.innerWidth) {
      return window.innerWidth;
    }
    return 0;
  }

  private setLayoutForWidth(width: number) {
    if (!width) {
      this.isMobile = false;
      this.profileLimit = 186;
      return;
    }
    this.isMobile = width < 1100;
    this.profileLimit = this.isMobile ? 124 : 186;
  }

  private getFirstSlotFromParts(parts: any[]) {
    if (!parts || !parts.length) {
      return '';
    }
    const allSlots = parts.reduce((list: string[], part: any) => {
      if (Array.isArray(part?.slots)) {
        return [...list, ...part.slots];
      }
      return list;
    }, []);
    if (!allSlots.length) {
      return '';
    }
    const sorted = allSlots.slice().sort((a: string, b: string) => moment(a, 'hh:mm A').diff(moment(b, 'hh:mm A')));
    return sorted[0];
  }

  private formatAppointmentText(total: number) {
    if (!total) {
      return 'No appts';
    }
    return `${total} ${total === 1 ? 'appt' : 'appts'}`;
  }

  private mergeConsecutiveUnavailable(days: any[]) {
    if (!Array.isArray(days) || !days.length) {
      return [];
    }
    const merged: any[] = [];
    let i = 0;
    while (i < days.length) {
      const current = days[i];
      if (current.totalSlots > 0) {
        merged.push(current);
        i += 1;
        continue;
      }
      const start = current;
      let end = current;
      let j = i + 1;
      while (j < days.length && days[j].totalSlots === 0) {
        end = days[j];
        j += 1;
      }
      if (start.date === end.date) {
        merged.push(start);
      } else {
        const startMoment = moment(start.date, 'YYYY-MM-DD');
        const endMoment = moment(end.date, 'YYYY-MM-DD');
        merged.push({
          ...start,
          id: `${start.date}_${end.date}`,
          date: start.date,
          label: `${startMoment.format('ddd')} - ${endMoment.format('ddd')}`,
          displayDate: `${startMoment.format('MMM D')} - ${endMoment.format('MMM D')}`,
          appointmentsText: 'No appts',
          firstSlot: '',
          parts: [],
          totalSlots: 0
        });
      }
      i = j;
    }
    return merged;
  }

  private computeFocusAreas(partner: any): string[] {
    if (!Array.isArray(partner?.specialization)) {
      return [];
    }
    return partner.specialization
      .filter((item: string) => !!item)
      .map((item: string) => this.replaceSplCharecters(item.toString()));
  }

  private updateFocusAreas() {
    if (this.partner) {
      this.focusAreas = this.partner?.specialization;
    }
    // this.focusAreas = this.computeFocusAreas(this.partner);
    const defaultVisible = this.focusChunk;
    this.desktopFocusVisible = Math.min(defaultVisible, this.focusAreas.length || defaultVisible);
    this.mobileFocusVisible = Math.min(defaultVisible, this.focusAreas.length || defaultVisible);
  }

  get roundedRatingScore(): number | null {
    const rating = this.practitioner_rating;
    let rawScore: any = null;

    if (rating && typeof rating === 'object') {
      rawScore = rating.rate ?? rating.rating ?? rating.average ?? rating.avg ?? null;
    } else if (typeof rating === 'number') {
      rawScore = rating;
    }

    if (typeof rawScore !== 'number') {
      return null;
    }

    return Math.round(rawScore);
  }

  private clearSelectedDay() {
    this.selectedDay = null;
    this.selectedDayParts = [];
    this.selectedDayFlatSlots = [];
  }

  flattenSlots(parts: any[]): { key: string; time: string }[] {
    if (!Array.isArray(parts)) {
      return [];
    }
    return parts.reduce((list: { key: string; time: string }[], part: any) => {
      const key = part?.key || '';
      const slots = Array.isArray(part?.slots) ? part.slots : [];
      const normalizedSlots = slots
        .filter((t: any) => !!t)
        .map((t: any) => (typeof t === 'string' ? t : t?.time || t?.start_time || ''))
        .filter((t: string) => !!t);
      const mapped = normalizedSlots.map((time: string) => ({ key, time }));
      return [...list, ...mapped];
    }, []);
  }

  trackSlot(index: number, slot: { key: string; time: string }) {
    return `${slot.key}-${slot.time}-${index}`;
  }
}
